import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StageBadge } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Send, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import EmployeePicker from "@/components/app/EmployeePicker";
import SearchablePicker, { projectMatch, renderProjectRow } from "@/components/app/SearchablePicker";

const STATUSES = ["Open", "Assigned", "In Progress", "Resolved", "Closed"];
const ACTIVE_STATUSES = ["Open", "Assigned", "In Progress"];
const COMPLETED_STATUSES = ["Resolved", "Closed"];

export default function Tickets() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("active"); // active | completed
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [searchParams] = useSearchParams();

  const load = () => api.get("/tickets").then((r) => setTickets(r.data));
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.role !== "customer" && u.active !== false))).catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    const id = searchParams.get("id");
    if (id) api.get(`/tickets/${id}`).then((r) => setSelected(r.data)).catch(() => {});
  }, [searchParams]);

  const assigneeName = (t) => {
    if (!t?.assigned_to) return "";
    return t.assigned_to_name || users.find((u) => u.id === t.assigned_to)?.name || t.assigned_to;
  };

  const updateAssignee = async (id, assigned_to) => {
    try {
      await api.patch(`/tickets/${id}`, { assigned_to });
      toast.success(assigned_to ? "Ticket assigned" : "Ticket unassigned");
      load();
      if (selected?.id === id) api.get(`/tickets/${id}`).then((r) => setSelected(r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assignment failed");
    }
  };

  // Reset status filter when switching tabs so a completed-only status
  // doesn't get carried over to the Active tab (and vice versa).
  useEffect(() => { setStatusFilter("all"); }, [tab]);

  const updateStatus = async (id, status) => {
    await api.patch(`/tickets/${id}`, { status });
    toast.success(`Status → ${status}`);
    load();
    if (selected?.id === id) api.get(`/tickets/${id}`).then((r) => setSelected(r.data));
  };

  const deleteTicket = async (id) => {
    if (!window.confirm("Delete this ticket and all its comments? This cannot be undone.")) return;
    try {
      await api.delete(`/tickets/${id}`);
      toast.success("Ticket deleted");
      if (selected?.id === id) setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const activeCount = useMemo(() => tickets.filter((t) => ACTIVE_STATUSES.includes(t.status)).length, [tickets]);
  const completedCount = useMemo(() => tickets.filter((t) => COMPLETED_STATUSES.includes(t.status)).length, [tickets]);

  const tabStatuses = tab === "active" ? ACTIVE_STATUSES : COMPLETED_STATUSES;

  const visibleTickets = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((t) => {
      if (!tabStatuses.includes(t.status)) return false;
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (assigneeFilter === "unassigned" && t.assigned_to) return false;
      if (assigneeFilter !== "all" && assigneeFilter !== "unassigned" && t.assigned_to !== assigneeFilter) return false;
      if (!q) return true;
      return (
        (t.customer_name || "").toLowerCase().includes(q) ||
        (t.title || "").toLowerCase().includes(q) ||
        (t.phone || "").toLowerCase().includes(q) ||
        (t.id || "").toLowerCase().includes(q)
      );
    });
  }, [tickets, tab, tabStatuses, statusFilter, assigneeFilter, search]);

  return (
    <div data-testid="tickets-page">
      <PageHeader
        eyebrow="After-Sales"
        title="Service Tickets"
        subtitle="Customer complaints from QR scans and direct entry. Response and resolution time tracked."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="new-ticket-button">
                <Plus className="w-4 h-4 mr-2" /> New Ticket
              </Button>
            </DialogTrigger>
            <NewTicketDialog onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />

      <div className="p-4 sm:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs value={tab} onValueChange={setTab}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <TabsList className="rounded-none border border-[#E5E7EB] bg-white p-0" data-testid="tickets-tabs">
                <TabsTrigger
                  value="active"
                  className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-4 h-9"
                  data-testid="tickets-tab-active"
                >
                  Active <span className="ml-2 text-[10px] opacity-70">{activeCount}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="completed"
                  className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-4 h-9"
                  data-testid="tickets-tab-completed"
                >
                  Completed <span className="ml-2 text-[10px] opacity-70">{completedCount}</span>
                </TabsTrigger>
              </TabsList>

              <div className="flex items-center gap-2 flex-1 sm:justify-end">
                <div className="relative flex-1 sm:max-w-xs">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search customer, title, phone…"
                    className="rounded-none h-9 pl-8 text-xs"
                    data-testid="tickets-search"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="rounded-none h-9 text-xs w-40" data-testid="tickets-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {tabStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                  <SelectTrigger className="rounded-none h-9 text-xs w-40" data-testid="tickets-assignee-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All assignees</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <TabsContent value="active" className="mt-0">
              <TicketTable
                tickets={visibleTickets}
                isAdmin={isAdmin}
                onRowClick={setSelected}
                onStatusChange={updateStatus}
                onAssign={updateAssignee}
                users={users}
                assigneeName={assigneeName}
                onDelete={deleteTicket}
              />
            </TabsContent>
            <TabsContent value="completed" className="mt-0">
              <TicketTable
                tickets={visibleTickets}
                isAdmin={isAdmin}
                onRowClick={setSelected}
                onStatusChange={updateStatus}
                onAssign={updateAssignee}
                users={users}
                assigneeName={assigneeName}
                onDelete={deleteTicket}
              />
            </TabsContent>
          </Tabs>
        </div>

        <div>
          {selected ? (
            <div className="flat-card p-6" data-testid="ticket-detail">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="label-eyebrow mb-2">#{selected.id.slice(0, 8)}</div>
                  <h3 className="text-xl font-black tracking-tight">{selected.title}</h3>
                  <div className="mt-2"><StageBadge stage={selected.status} /></div>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => deleteTicket(selected.id)}
                    className="text-xs uppercase tracking-widest font-bold text-[#DC2626] hover:underline shrink-0"
                    data-testid="ticket-delete-detail"
                  >
                    Delete
                  </button>
                )}
              </div>
              <div className="mt-4 text-sm whitespace-pre-wrap">{selected.description}</div>
              <div className="mt-4 swiss-divider" />
              <div className="text-xs text-[#4B5563] mt-4">Customer: <b>{selected.customer_name}</b></div>
              {selected.phone && <div className="text-xs text-[#4B5563]">Phone: {selected.phone}</div>}
              {selected.location && <div className="text-xs text-[#4B5563]" data-testid="ticket-detail-location">Location: {selected.location}</div>}
              <div className="mt-4">
                <div className="label-eyebrow mb-2">Assigned to</div>
                <AssigneeSelect ticket={selected} users={users} assigneeName={assigneeName} onAssign={updateAssignee} testId="ticket-detail-assignee" className="w-full" />
              </div>
              <div className="mt-4">
                <div className="label-eyebrow mb-2">History</div>
                <ul className="text-xs space-y-1">
                  {(selected.history || []).map((h, i) => (
                    <li key={`${h.at}-${i}`}>· {fmtDate(h.at)} — {h.event} <span className="text-[#9CA3AF]">({h.by})</span></li>
                  ))}
                </ul>
              </div>
              <TicketComments ticketId={selected.id} currentUser={user} />
            </div>
          ) : (
            <div className="flat-card p-8 text-center label-eyebrow">Select a ticket to inspect</div>
          )}
        </div>
      </div>
    </div>
  );
}

function AssigneeSelect({ ticket, users, assigneeName, onAssign, testId, className }) {
  const known = users.some((u) => u.id === ticket.assigned_to);
  const employees = ticket.assigned_to && !known
    ? [{ id: ticket.assigned_to, name: assigneeName(ticket), role: "" }, ...users]
    : users;
  return (
    <div className={className}>
      <EmployeePicker
        employees={employees}
        value={ticket.assigned_to || ""}
        onChange={(id) => { if (id !== (ticket.assigned_to || "")) onAssign(ticket.id, id || ""); }}
        placeholder="Assign to…"
        testid={testId}
      />
    </div>
  );
}

function TicketTable({ tickets, isAdmin, onRowClick, onStatusChange, onAssign, users, assigneeName, onDelete }) {
  return (
    <div className="border border-[#E5E7EB] overflow-x-auto" data-testid="tickets-table">
      <table className="w-full text-sm">
        <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
          <tr>
            {["#", "Customer", "Title", "Priority", "Status", "Assigned to", "Created", "", ...(isAdmin ? [""] : [])].map((h, i) => (
              <th key={i} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => (
            <tr key={t.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer" onClick={() => onRowClick(t)} data-testid={`ticket-row-${t.id}`}>
              <td className="px-4 py-3 text-xs font-mono">{t.id.slice(0, 8)}</td>
              <td className="px-4 py-3 font-semibold">{t.customer_name}</td>
              <td className="px-4 py-3 text-xs">{t.title}</td>
              <td className="px-4 py-3 text-xs">{t.priority}</td>
              <td className="px-4 py-3"><StageBadge stage={t.status} /></td>
              <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                <AssigneeSelect ticket={t} users={users} assigneeName={assigneeName} onAssign={onAssign} testId={`ticket-assignee-${t.id}`} className="w-44" />
              </td>
              <td className="px-4 py-3 text-xs">{fmtDate(t.created_at)}</td>
              <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                <Select value={t.status} onValueChange={(v) => onStatusChange(t.id, v)}>
                  <SelectTrigger className="rounded-none h-8 text-xs w-32" data-testid={`ticket-status-${t.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              {isAdmin && (
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => onDelete(t.id)}
                    className="text-[#9CA3AF] hover:text-[#DC2626] transition"
                    data-testid={`ticket-delete-${t.id}`}
                    aria-label="Delete ticket"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              )}
            </tr>
          ))}
          {tickets.length === 0 && <tr><td colSpan={isAdmin ? 9 : 8} className="text-center text-sm text-[#9CA3AF] py-12" data-testid="tickets-empty">No tickets match your filters.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function NewTicketDialog({ onDone }) {
  const [form, setForm] = useState({ project_id: "", customer_name: "", phone: "", location: "", title: "", description: "", priority: "Medium" });
  const set = (k) => (v) => setForm({ ...form, [k]: v.target ? v.target.value : v });

  const submit = async () => {
    if (!form.customer_name || !form.title) { toast.error("Customer and title required"); return; }
    await api.post("/tickets", form);
    toast.success("Ticket created");
    onDone();
  };

  // Selected project object resolved by the picker (optionsEndpoint mode).
  const [selectedProject, setSelectedProject] = useState(null);

  const linkedProject = selectedProject && selectedProject.id === form.project_id ? selectedProject : null;

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-lg">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">New Service Ticket</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <div>
          <Label className="label-eyebrow">Project (optional)</Label>
          <SearchablePicker
            items={[]}
            value={form.project_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            onSelectItem={(p) => {
              setSelectedProject(p);
              if (p) setForm((prev) => ({
                ...prev,
                customer_name: p.customer_name || prev.customer_name,
                location: p.location || prev.location,
              }));
            }}
            optionsEndpoint="/projects/options"
            renderRow={renderProjectRow}
            selectedLabel={(p) => `${p.customer_name} — ${p.qr_code}`}
            placeholder="Search by customer, QR or location…"
            testid="ticket-project-select"
            emptyHint="No projects match your search."
          />
        </div>
        <Field label="Customer Name" value={form.customer_name} onChange={set("customer_name")} testid="ticket-customer" />
        <Field label="Phone" value={form.phone} onChange={set("phone")} />
        <div>
          <Label className="label-eyebrow">Location</Label>
          <Input
            value={form.location}
            onChange={set("location")}
            placeholder={linkedProject ? "Auto-filled from project" : "Add site location for this ticket"}
            className="rounded-none mt-2 h-10"
            data-testid="ticket-location"
          />
          {linkedProject && form.location === linkedProject.location && (
            <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] mt-1">Pulled from linked project. Edit if needed.</div>
          )}
        </div>
        <Field label="Title" value={form.title} onChange={set("title")} testid="ticket-title" />
        <div>
          <Label className="label-eyebrow">Description</Label>
          <Textarea value={form.description} onChange={set("description")} rows={4} className="rounded-none mt-2" data-testid="ticket-description" />
        </div>
        <div>
          <Label className="label-eyebrow">Priority</Label>
          <Select value={form.priority} onValueChange={set("priority")}>
            <SelectTrigger className="rounded-none mt-2 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>{["Low", "Medium", "High", "Critical"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="ticket-submit">Create Ticket</Button>
      </div>
    </DialogContent>
  );
}
function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input value={value} onChange={onChange} className="rounded-none mt-2 h-10" data-testid={testid} />
    </div>
  );
}

function TicketComments({ ticketId, currentUser }) {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = currentUser?.role === "admin";

  const load = () => api.get(`/tickets/${ticketId}/comments`).then((r) => setComments(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  const submit = async () => {
    if (!body.trim()) { toast.error("Comment cannot be empty"); return; }
    setBusy(true);
    try {
      await api.post(`/tickets/${ticketId}/comments`, { body });
      setBody("");
      load();
      toast.success("Comment posted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Post failed");
    } finally { setBusy(false); }
  };

  const del = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try { await api.delete(`/tickets/${ticketId}/comments/${cid}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="mt-6 pt-6 border-t border-[#E5E7EB]" data-testid="ticket-comments">
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow">Internal Comments</div>
        <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 bg-[#FEF3C7] text-[#92400E]">Not Visible to Customer</span>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {comments.length === 0 && (
          <div className="text-xs text-[#9CA3AF] italic py-2" data-testid="ticket-comments-empty">No comments yet.</div>
        )}
        {comments.map((c) => {
          const canDelete = isAdmin || c.by_id === currentUser?.id;
          return (
            <div key={c.id} className="border-l-2 border-[#E5E7EB] pl-3 py-1 group" data-testid={`ticket-comment-${c.id}`}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-xs">
                  <span className="font-semibold text-[#0a0a0a]">{c.by_name}</span>
                  <span className="text-[10px] uppercase tracking-widest text-[#9CA3AF] ml-2">{c.by_role}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap">{fmtDate(c.created_at)}</span>
                  {canDelete && (
                    <button
                      onClick={() => del(c.id)}
                      className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-[#EF4444] transition"
                      data-testid={`ticket-comment-delete-${c.id}`}
                      aria-label="Delete comment"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <div className="text-sm text-[#1F2937] mt-1 whitespace-pre-wrap break-words">{c.body}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex gap-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Internal note for the team…"
          rows={2}
          className="rounded-none flex-1"
          data-testid="ticket-comment-input"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
        />
        <Button
          onClick={submit}
          disabled={busy || !body.trim()}
          className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-[10px] font-bold h-auto px-4"
          data-testid="ticket-comment-submit"
        >
          <Send className="w-3.5 h-3.5 mr-1" /> Post
        </Button>
      </div>
      <div className="text-[10px] text-[#9CA3AF] mt-1">Tip: ⌘/Ctrl + Enter to submit. Customers never see these.</div>
    </div>
  );
}
